import { Dispatch, SetStateAction } from 'react';
import { FieldPath, UseFormReturn, useWatch } from 'react-hook-form';
import { CheckboxField, TextInputField } from '@brighte/form-components';
import { IncomeVerificationType } from '@brighte/validation';
import { OccupationField } from '@components/OccupationField';
import { Occupation } from '@graphql/index';
import { Field } from '@spark-web/field';
import { useApplicationData } from '../../provider';
import { INCOME_VERIFICATION, LABELS, ManualOccupationJobTitleMatchingType } from '../../utils';

type SelfEmployedProps = Pick<
  UseFormReturn<IncomeVerificationType>,
  'control' | 'setValue' | 'register' | 'clearErrors'
> & {
  fields: string[];
  setOccupationIncomeThreshold: Dispatch<SetStateAction<number>>;
  occupations: Occupation[];
  isOccupationLoading: boolean;
  isOccupationFieldDisplayed: boolean;
  usingOpenBanking?: boolean;
  manualOccupationJobTitle: ManualOccupationJobTitleMatchingType;
  setManualOccupationJobTitle: Dispatch<SetStateAction<ManualOccupationJobTitleMatchingType>>;
};

function SelfEmployed({
  fields,
  control,
  setValue,
  register,
  clearErrors,
  setOccupationIncomeThreshold,
  occupations,
  isOccupationLoading,
  isOccupationFieldDisplayed,
  usingOpenBanking,
  manualOccupationJobTitle,
  setManualOccupationJobTitle,
}: SelfEmployedProps) {
  const { BUSINESS_PHONE_NUMBER, OCCUPATION_FIELD } = LABELS;
  const { SAME_AS_MOBILE } = INCOME_VERIFICATION;
  const contactDetails = useApplicationData('ContactDetails');

  const isSameMobileField = 'applicant.employment.selfEmployed.isSameMobileNumber';
  const phoneNumberField = 'applicant.employment.selfEmployed.phone';

  const isSameMobileNumber = useWatch({
    control,
    name: isSameMobileField,
  });

  const handleSameMobileNumber = e => {
    const mobileNumber = contactDetails?.mobile ?? '';
    setValue(phoneNumberField, e.target.value ? mobileNumber : '');
  };

  return (
    <>
      {fields.includes(phoneNumberField) && usingOpenBanking === false ? (
        <Field label={BUSINESS_PHONE_NUMBER}>
          <CheckboxField
            name={isSameMobileField}
            control={control}
            label={SAME_AS_MOBILE}
            onChange={handleSameMobileNumber}
          />
          {fields.includes(isSameMobileField) ? (
            <TextInputField
              name={phoneNumberField}
              control={control}
              label=""
              FieldProps={{ disabled: Boolean(isSameMobileNumber) }}
            />
          ) : null}
        </Field>
      ) : null}
      {isOccupationFieldDisplayed && usingOpenBanking === false && (
        <OccupationField<IncomeVerificationType>
          setValue={setValue}
          clearErrors={clearErrors}
          register={register}
          control={control}
          name={`applicant.employment.selfEmployed` as FieldPath<IncomeVerificationType>}
          label={OCCUPATION_FIELD}
          setOccupationIncomeThreshold={setOccupationIncomeThreshold}
          occupations={occupations}
          isOccupationLoading={isOccupationLoading}
          manualOccupationJobTitle={manualOccupationJobTitle}
          setManualOccupationJobTitle={setManualOccupationJobTitle}
        />
      )}
      <input type="hidden" {...register('applicant.employment.selfEmployed.entityName')} />
    </>
  );
}

export default SelfEmployed;
